import { ShieldCheck } from "lucide-react";

import { WizardDefinition } from "../framework/types";
import { wizardMascots } from "../mascots/mascots";
import { CONSENT_MANAGER_WIZARD_TYPE } from "./constants";
import { ESSENTIAL_ALWAYS_SENT } from "./copy";
import { BannerStep } from "./steps/BannerStep";
import { CategorizeStep } from "./steps/CategorizeStep";
import { PublishStep } from "./steps/PublishStep";
import { SetUpStep } from "./steps/SetUpStep";
import { TargetsStep } from "./steps/TargetsStep";
import { TurnOnStep } from "./steps/TurnOnStep";
import { ConsentDraftState, currentTargeting } from "./targeting";
import { ConsentManagerData } from "./types";
import { useConsentDraftState } from "./useFreshpaintConsent";

const isFreshpaintSetup = (data: ConsentManagerData) => !data.managedBy;
// With nothing stored and the draft unknown, a resumed session may be on the
// targets step, so that step stays applicable until the draft loads.
const isTargeted = (data: ConsentManagerData, draftState: ConsentDraftState) =>
  isFreshpaintSetup(data) &&
  ((!draftState.known && data.targeting === null) ||
    currentTargeting(data, draftState).preset !== "everywhere");

export const consentManagerWizard: WizardDefinition<
  ConsentManagerData,
  ConsentDraftState
> = {
  type: CONSENT_MANAGER_WIZARD_TYPE,
  title: "Publish your consent banner",
  description:
    "Set up the Freshpaint Consent Manager so events follow visitor consent",
  Icon: ShieldCheck,
  mascot: wizardMascots[CONSENT_MANAGER_WIZARD_TYPE],
  searchKeys: ["consent", "cookie", "banner", "privacy", "gdpr", "cmp"],
  createInitialData: () => ({
    consentModel: "opt-in",
    targeting: null,
    categoryDraft: {},
    privacyPolicyUrl: null,
    privacyPolicyLinkText: null,
    managedBy: null,
  }),
  getCompletionSummary: (data) =>
    data.managedBy
      ? {
          title: `${data.managedBy} manages consent`,
          description:
            "Freshpaint already follows your visitors’ consent choices in this environment.",
        }
      : {
          title: "Your consent banner is live",
          description: `Visitors see your banner on their next page view, and Freshpaint sends their events only to the destinations their consent choices allow. ${ESSENTIAL_ALWAYS_SENT}`,
        },
  // Unsaved edits are dropped across reloads so they can't overwrite values
  // changed on the Consent Manager page in the meantime. Targeting is kept
  // because currentTargeting ignores it once the draft's targets change.
  sanitizeForStorage: (data) => ({
    ...data,
    categoryDraft: {},
    privacyPolicyUrl: null,
    privacyPolicyLinkText: null,
  }),
  useStepContext: useConsentDraftState,
  // When another vendor already manages consent, the wizard ends after the
  // first step.
  steps: [
    {
      id: "set-up",
      phase: "Set up",
      title: "Create banner",
      Component: SetUpStep,
    },
    {
      id: "targets",
      isApplicable: isTargeted,
      phase: "Set up",
      title: "Set targeted rules",
      Component: TargetsStep,
    },
    {
      id: "categorize",
      isApplicable: isFreshpaintSetup,
      phase: "Protect",
      title: "Categorize destinations",
      Component: CategorizeStep,
    },
    {
      id: "banner",
      isApplicable: isFreshpaintSetup,
      phase: "Customize",
      title: "Link privacy policy",
      Component: BannerStep,
    },
    {
      id: "publish",
      isApplicable: isFreshpaintSetup,
      phase: "Go live",
      title: "Publish",
      Component: PublishStep,
    },
    {
      id: "turn-on",
      isApplicable: isFreshpaintSetup,
      phase: "Go live",
      title: "Turn on",
      Component: TurnOnStep,
    },
  ],
};
